import { DialogComponent } from '@theme/dialog';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} Store
 * @property {string} id
 * @property {string} name
 * @property {string} province
 * @property {string} address
 * @property {string | null} phone
 * @property {string | null} whatsapp_url
 * @property {string} maps_url
 * @property {string | null} opening_hours
 * @property {number | null} available
 * @property {'in_stock' | 'low_stock' | 'out_of_stock'} status
 *
 * @typedef {object} Availability
 * @property {{ total_stores: number, stores_in_stock: number }} summary
 * @property {{ name: string }[]} provinces
 * @property {Store[]} stores
 */

/** Shared across instances so the quick-add modal and product page reuse responses. */
const responseCache = new Map();

const ALL = '__all__';

const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );

const ICON_WHATSAPP =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.26-.46-2.39-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.27-.2-.57-.35zM12.05 21.79h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 0 1-1.51-5.26c0-5.45 4.44-9.88 9.89-9.88 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.45-4.44 9.88-9.88 9.88zm8.41-18.3A11.82 11.82 0 0 0 12.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.17-3.48-8.42z"/></svg>';
const ICON_PIN =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/></svg>';

/**
 * "AVAILABLE IN STORES" — per-store availability for the selected variant.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} list
 * @property {HTMLElement} provinces
 * @property {HTMLInputElement} onlyInStock
 * @property {HTMLElement} [productInfo]
 * @property {HTMLElement} [summary]
 *
 * @extends DialogComponent<Refs>
 */
class StoreAvailability extends DialogComponent {
  requiredRefs = ['dialog', 'list', 'provinces', 'onlyInStock'];

  /** @type {Availability | null} */
  #data = null;
  /** @type {string | undefined} */
  #dataVariantId;
  #skeletonHtml = '';
  #province = ALL;
  #requestId = 0;
  /** @type {Element | null} */
  #eventTarget = null;

  connectedCallback() {
    super.connectedCallback();

    this.#skeletonHtml = this.refs.list.innerHTML;
    this.#eventTarget = this.closest('.shopify-section, dialog');
    this.#eventTarget?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    if (this.dataset.showSummary === 'true') {
      const load = () => this.#updateSummary();
      'requestIdleCallback' in window ? requestIdleCallback(load, { timeout: 2000 }) : setTimeout(load, 200);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /** Opens the modal and loads availability for the current variant. */
  openAvailability() {
    this.showDialog();
    this.#load();
  }

  /**
   * Updates the chips in place: replacing the clicked button would detach it from the DOM,
   * and DialogComponent would then treat the click as outside the dialog and close it.
   * @param {Event} event
   */
  selectProvince(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    this.#province = target.dataset.province || ALL;

    for (const chip of this.refs.provinces.querySelectorAll('[data-province]')) {
      chip.setAttribute('aria-pressed', String(chip.getAttribute('data-province') === this.#province));
    }

    this.#renderList();
  }

  handleFilterChange() {
    this.#render();
  }

  /** Deferred for the same reason as selectProvince: #load replaces the retry button. */
  retry() {
    requestAnimationFrame(() => this.#load());
  }

  /**
   * Keeps the variant ID and product summary in sync with the variant picker.
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.variantId = String(variantId);

    const newInfo = event.detail.data.html?.querySelector(
      `store-availability[data-block-id="${this.dataset.blockId}"] [ref="productInfo"]`
    );
    if (newInfo && this.refs.productInfo) {
      morph(this.refs.productInfo, newInfo, { childrenOnly: true });
    }

    if (this.refs.dialog.open) this.#load();
    if (this.dataset.showSummary === 'true') this.#updateSummary();
  };

  /**
   * @param {string} variantId
   * @returns {Promise<Availability>}
   */
  #fetchAvailability(variantId) {
    const { apiBase, threshold } = this.dataset;
    const url = `${apiBase}/availability/${encodeURIComponent(variantId)}?threshold=${encodeURIComponent(threshold ?? '')}`;

    if (!responseCache.has(url)) {
      const request = fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Store availability request failed (${response.status})`);
          return response.json();
        })
        .then((payload) => payload.data)
        .catch((error) => {
          responseCache.delete(url);
          throw error;
        });

      responseCache.set(url, request);
    }

    return responseCache.get(url);
  }

  async #load() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const requestId = ++this.#requestId;
    const { list } = this.refs;

    if (this.#dataVariantId !== variantId) {
      list.setAttribute('aria-busy', 'true');
      list.innerHTML = this.#skeletonHtml;
    }

    try {
      const data = await this.#fetchAvailability(variantId);
      if (requestId !== this.#requestId) return;

      this.#data = data;
      this.#dataVariantId = variantId;
      this.#render();
    } catch (error) {
      if (requestId !== this.#requestId) return;

      console.error(error);
      this.#data = null;
      this.#dataVariantId = undefined;
      this.#renderError();
    }
  }

  async #updateSummary() {
    const { summary } = this.refs;
    const variantId = this.dataset.variantId;
    if (!summary || !variantId) return;

    try {
      const data = await this.#fetchAvailability(variantId);
      if (this.dataset.variantId !== variantId) return;

      const { stores_in_stock: count, total_stores: total } = data.summary;
      summary.textContent =
        count > 0
          ? (this.dataset.labelSummary || '').replace('[count]', String(count)).replace('[total]', String(total))
          : this.dataset.labelSummaryNone || '';
    } catch {
      summary.textContent = '';
    }
  }

  /** @param {Store} store */
  #matchesStockFilter = (store) => !this.refs.onlyInStock.checked || store.status !== 'out_of_stock';

  #render() {
    if (!this.#data) return;

    this.#renderProvinces();
    this.#renderList();
  }

  #renderProvinces() {
    if (!this.#data) return;

    const stores = this.#data.stores.filter(this.#matchesStockFilter);

    const provinceNames = this.#data.provinces.map((province) => province.name);
    if (this.#province !== ALL && !provinceNames.includes(this.#province)) this.#province = ALL;

    const chip = (value, label, count) => `
      <button
        type="button"
        class="store-availability__province"
        data-province="${escapeHtml(value)}"
        aria-pressed="${this.#province === value}"
        on:click="/selectProvince"
      >${escapeHtml(label)}<span class="store-availability__province-count">(${count})</span></button>`;

    this.refs.provinces.innerHTML = [
      chip(ALL, this.dataset.labelAll, stores.length),
      ...provinceNames.map((name) => chip(name, name, stores.filter((store) => store.province === name).length)),
    ].join('');
  }

  #renderList() {
    if (!this.#data) return;

    const { list } = this.refs;
    const stores = this.#data.stores.filter(this.#matchesStockFilter);
    const visible = this.#province === ALL ? stores : stores.filter((store) => store.province === this.#province);

    list.setAttribute('aria-busy', 'false');
    list.innerHTML = visible.length
      ? visible.map((store) => this.#storeCard(store)).join('')
      : `<p class="store-availability__empty">${escapeHtml(this.dataset.labelEmpty)}</p>`;
  }

  /** @param {Store} store */
  #storeCard(store) {
    const { dataset } = this;
    const statusLabel = {
      in_stock: dataset.labelInStock,
      low_stock: dataset.labelLowStock,
      out_of_stock: dataset.labelOutOfStock,
    }[store.status];

    const hours = store.opening_hours || dataset.defaultHours;
    const contactUrl = store.whatsapp_url || (store.phone ? `tel:${store.phone}` : null);
    const showQuantity = dataset.showQuantity === 'true' && store.available !== null;

    return `
      <article class="store-availability__card store-availability__card--${store.status}">
        <div class="store-availability__card-header">
          <p class="store-availability__store-name">${escapeHtml(store.name)}</p>
          <span class="store-availability__status store-availability__status--${store.status}">${escapeHtml(statusLabel)}</span>
        </div>
        <p class="store-availability__meta">${escapeHtml(store.address)}</p>
        ${hours ? `<p class="store-availability__meta">${escapeHtml(dataset.labelHours)} ${escapeHtml(hours)}</p>` : ''}
        ${
          showQuantity
            ? `<p class="store-availability__meta">${escapeHtml((dataset.labelQuantity || '').replace('[count]', String(store.available)))}</p>`
            : ''
        }
        <div class="store-availability__actions">
          ${
            contactUrl
              ? `<a class="store-availability__action store-availability__action--primary" href="${escapeHtml(contactUrl)}" target="_blank" rel="noopener">${ICON_WHATSAPP}${escapeHtml(dataset.labelContact)}</a>`
              : ''
          }
          <a class="store-availability__action store-availability__action--secondary" href="${escapeHtml(store.maps_url)}" target="_blank" rel="noopener">${ICON_PIN}${escapeHtml(dataset.labelDirections)}</a>
        </div>
      </article>`;
  }

  #renderError() {
    const { list, provinces } = this.refs;

    provinces.innerHTML = '';
    list.setAttribute('aria-busy', 'false');
    list.innerHTML = `
      <div class="store-availability__empty">
        <p>${escapeHtml(this.dataset.labelError)}</p>
        <button type="button" class="button-secondary" on:click="/retry">${escapeHtml(this.dataset.labelRetry)}</button>
      </div>`;
  }
}

if (!customElements.get('store-availability')) {
  customElements.define('store-availability', StoreAvailability);
}
